import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Grid, Container, IconButton } from "@mui/material";
import { useAlertService } from "../../hooks/useAlertService";
import InnerMMSettings from "../inner/innerMMsettings";
import { deleteConnection, getListConnectionPair, toggleConnectionState } from "../../api/listPairs";
import { ConnectionStatusMessage, MMConnectionWithStatus } from "../../api/types";
import { CardContainer, CenteredContainer } from "./Pairs.styled";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { ConnectionStatusWS } from "../../api/wsApi";
import { ConnectionPairCard } from "../../components/ConnectionPairCard";

export function Pairs() {
  const [data, setData] = useState<MMConnectionWithStatus[]>([]);
  const [selectedUser, setSelectedUser] = useState<MMConnectionWithStatus | null>(null);
  const [exchangeFilter, setExchangeFilter] = useState("");
  const [pairNameFilter, setPairNameFilter] = useState("");
  const [isActiveFilter, setIsActiveFilter] = useState("");
  const [tick, setTick] = useState(0); // используется для перерисовки
  const { showAlert } = useAlertService();

  // Хранилище статусов WebSocket по id (Map)
  const statusMapRef = useRef<Map<string, ConnectionStatusMessage["status"]>>(new Map());

  // Мемоизируем результат statusMap (чтобы не вызывать Map.get каждый раз)
  const statusMap = useMemo(() => {
    const result = new Map(statusMapRef.current); // создаём snapshot
    return result;
  }, [tick]);

  // Функция фильтрации полученных данных
  const filteredData = useMemo(() => {
    return data.filter(pair => {
      return (
          (!exchangeFilter || pair.exchange === exchangeFilter) &&
          (!pairNameFilter || pair.pair_name.includes(pairNameFilter)) &&
          (isActiveFilter === "" || pair.is_active === (isActiveFilter === "true"))
      );
    });
  }, [data, exchangeFilter, pairNameFilter, isActiveFilter]);

  // Функция переключение on/off
  const handleToggle = useCallback(async (id: string, newState: boolean) => {
    try {
      await toggleConnectionState(id);
      setData((prevData) =>
          prevData.map((item) =>
              item.id === id ? { ...item, is_active: newState } : item
          )
      );
      showAlert({
        type: "success",
        message: newState
            ? "The bot has been successfully enabled"
            : "The bot has been successfully disabled",
      });
    } catch {
      showAlert({
        type: "error",
        message: "Failed to change bot state! Try again in 100 seconds!",
      });
    }
  }, [showAlert]);

  // Функция сброса фильтров и перезапроса данных
  const handleResetFilters = async () => {
    setExchangeFilter("");
    setPairNameFilter("");
    setIsActiveFilter("");
    try {
      const pairs = await getListConnectionPair();
      setData(pairs as MMConnectionWithStatus[]);
    } catch {
      setData([]);
    }
  };

  // Выбор конкретного пользователя
  const handleSelectUser = useCallback((user: MMConnectionWithStatus) => {
    setSelectedUser(user);
  }, []);

  const handleDelete = useCallback(async (pair: MMConnectionWithStatus) => {
    try {
      await deleteConnection(pair.id);
      statusMapRef.current.delete(pair.id);
      setData((current) => current.filter((item) => item.id !== pair.id));
      showAlert({
        type: "success",
        message: `${pair.exchange} ${pair.pair_name} has been deleted`,
      });
    } catch (error) {
      showAlert({
        type: "error",
        message: "Failed to delete the pair. Make sure it is offline and try again.",
      });
      throw error;
    }
  }, [showAlert]);

  // Возврат к списку пар
  const handleReturn = () => {
    setSelectedUser(null);
  };

  useEffect(() => {
    let wsInstance: ConnectionStatusWS;

    const fetchData = async () => {
      try {
        const pairs = await getListConnectionPair();
        setData(pairs as MMConnectionWithStatus[]);

        wsInstance = new ConnectionStatusWS();
        wsInstance.connect((message: ConnectionStatusMessage) => {
          const prevStatus = statusMapRef.current.get(message.id);

          // Если статус изменился или его не было — обновляем
          if (prevStatus !== message.status) {
            console.log(`[WS] Updated status for ${message.id}: ${prevStatus} → ${message.status}`);
            statusMapRef.current.set(message.id, message.status);
            setTick((t) => t + 1);
          }
        });
      } catch {
        showAlert({
          type: "error",
          message: "Failed to fetch connection pairs",
        });
      }
    };

    fetchData();

    return () => {
      if (wsInstance) wsInstance.disconnect();
    };
  }, []);

  return selectedUser ? (
      <CenteredContainer>
        <InnerMMSettings return={handleReturn} data={selectedUser} />
      </CenteredContainer>
  ) : (
      <CardContainer>

        <Container maxWidth={false} disableGutters>

          {/* Фильтр по бирже */}
          <Grid container spacing={2} mb={2} alignItems="center">
            <Grid item xs={12} sm={3.5}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Exchange</InputLabel>
                <Select value={exchangeFilter} onChange={(e) => setExchangeFilter(e.target.value)} label="Exchange">
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(data.map(pair => pair.exchange))).map(exchange => (
                      <MenuItem key={exchange} value={exchange}>{exchange}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Фильтр по названию пары */}
            <Grid item xs={12} sm={3.5}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Pair Name</InputLabel>
                <Select value={pairNameFilter} onChange={(e) => setPairNameFilter(e.target.value)} label="Pair Name">
                  <MenuItem value="">All</MenuItem>
                  {Array.from(new Set(data.map(pair => pair.pair_name))).map(pairName => (
                      <MenuItem key={pairName} value={pairName}>{pairName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            {/* Фильтр по статусу */}
            <Grid item xs={12} sm={3.5}>
              <FormControl fullWidth variant="outlined" size="small">
                <InputLabel>Status</InputLabel>
                <Select value={isActiveFilter} onChange={(e) => setIsActiveFilter(e.target.value)} label="Status">
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="true">Online</MenuItem>
                  <MenuItem value="false">Offline</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Кнопка сброса фильтров */}
            <Grid item xs={12} sm={1.5} display="flex" justifyContent="flex-end">
              <IconButton onClick={handleResetFilters}>
                <RestartAltIcon />
              </IconButton>
            </Grid>
          </Grid>
        </Container>

        {/* Список карточек пар */}
        {filteredData.map((pair) => (
            <ConnectionPairCard
                key={pair.id}
                pair={pair}
                status={statusMap.get(pair.id)}
                onToggle={handleToggle}
                onSelect={handleSelectUser}
                onDelete={handleDelete}
            />
        ))}
      </CardContainer>
  );
}
